import React, { useState } from 'react'
import {useTranslation} from 'react-i18next'
import {useSelector, useDispatch, shallowEqual} from 'react-redux'
import styled from 'styled-components'
import {FaTelegram} from 'react-icons/fa'
import {FiMenu, FiUserPlus} from 'react-icons/fi'
import {MdHelpOutline, MdOutlineHelp} from 'react-icons/md'

import { BurgerToggle } from './BurgerToggle'
import { Languages } from '../../enums'
import {useAppSelector} from '../../redux/store'
import {getAvatar, UserAction} from '../../redux/reducers/user'
import { IconDocumentation, IconDown, IconLanguage, IconLogout, IconProfile, IconSupport, IconTopUp, IconUp } from '../icons'
import { ModalTopUp } from '../modals'
import './styles.css'
import Dropdown5 from './dropdown-5'
import {ButtonSecondary} from "../button";

const NavBar = styled.div`
	position: relative;
	display: flex;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 14px;

	color: white;
	font-family: "Basis Grotesque Pro";
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 100%; /* 16px */
	letter-spacing: 0.48px;
	text-transform: uppercase;
`

const NavItem = styled.div`
	position: relative;
	min-height: 40px;
	display: inline-flex;
	justify-content: center;
	align-items: center;

	&[data-flex="true"] {
		flex-grow: 1;
		flex-shrink: 1;
	}

	&[data-sidebar-toggler="true"] {
		position: absolute;
    /* top: calc( +24px + 10px );
    left: calc( -26px  ); */
		top: calc(-24px - 10px);
		left: calc(-28px);
		transition: all 0.4s;
		opacity: 1;
			
		&:hover {
				opacity: 0.75;
		}
	}
`

const NavDelimiter = styled(NavItem)`
	background: rgba(238, 238, 238, 0.05);
	width: 2px;
`

const NavName = styled.span`
	color: #FFF;
	font-family: 'Druk Wide', sans-serif;
	font-size: 20px;
	font-style: normal;
	font-weight: 500;
	line-height: 100%; /* 20px */
	letter-spacing: -0.6px;
	text-transform: uppercase;
`

const NavLink = styled(ButtonSecondary)`
	padding: 0px 16px;
`

const NavDropdown = styled.button`
	height: 40px;
	padding: 0px 10px;
	display: inline-flex;
	justify-content: center;
	align-items: center;
	gap: 4px;
	border-radius: 1px;
	border: 1px solid transparent;
	background: var(--it-bg-buttons);
	transition: all 0.4s;
		
	&[data-active="true"] {
		border: 1px solid var(--it-accent-blue);
	}
		
	&[data-background="false"] {
		background: unset;
	}

	&[data-opacity-hover="true"] {
		opacity: 1;
			
		&:hover {
				opacity: 0.75;
		}
	}

	&[data-color-hover="true"] {
		svg path {
				transition: all 0.4s;
				fill: var(--it-text-white);
		}

		&:hover {
				color: var(--it-text-black);
				background: var(--it-accent-blue);

				svg path {
						fill: var(--it-text-black);
				}
		}
	}
`

const NavDropdownList = styled.div`
	position: absolute;
  right: 0;
	top: calc(100% + 10px);
	z-index: 1;
	min-width: 120px;
	padding: 8px;
	background: rgba(238, 238, 238, 0.03);
	backdrop-filter: blur(50px);
	border: 1px solid rgba(255, 255, 255, 0.06);
	display: flex;
	flex-flow: column nowrap;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 1px;
	&[data-profile="true"] {
		text-transform: none;
		width: calc(100% - 20px);
		margin-right: 10px;
	}
`

const NavDropdownLink = styled.a`
	&:hover {
		background: rgba(238, 238, 238, 0.05);
	}
	border-radius: 1px;
	padding: 10px;
	width: 100%;
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;
	gap: 12px;
	flex-grow: 1;
	flex-shrink: 1;
	border: 1px solid transparent;
	&[data-active="true"], &:hover {
		color: rgba(255, 255, 255, 1);
		background: rgba(50, 160, 229, 0.10);
		backdrop-filter: blur(50px);
		border: 1px solid rgba(50, 160, 229, 0.20);
	}
`

const NavProfile = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`

export const NavAvatar = styled.div`
	--avatar-size: 40px;
	width: var(--avatar-size);
	height: var(--avatar-size);
	aspect-ratio: 1/1;
	border-radius: var(--avatar-size);
	/* background: url({avatar}) lightgray 50% / cover no-repeat, #D9D9D9; */
	background-size: cover;
	background-position: center center;
	background-repeat: no-repeat;
	//filter: grayscale(50%);
	background-color: #D9D9D9;
	object-fit: cover;
`

const NavProfileInfo = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 8px;
`

const NavProfileName = styled.div`
	color: #FFF;
	font-family: 'Druk Wide', sans-serif;
	font-size: 14px;
	font-style: normal;
	font-weight: 500;
	line-height: 120%; /* 16.8px */
	letter-spacing: 0.42px;
	text-transform: uppercase;
`

const NavProfileBalance = styled.div`
	align-self: stretch;
	color: rgba(255, 255, 255, 0.60);
	font-family: "Basis Grotesque Pro";
	font-size: 12px;
	font-style: normal;
	font-weight: 400;
	line-height: 100%; /* 12px */
	letter-spacing: 0.36px;
	text-transform: uppercase;
`


export const Navbar = () => {
	const { t, i18n } = useTranslation()
	const refLanguage = React.useRef(null)
	const refProfile = React.useRef(null)
	const [showLanguage, setShowLanguage] = useState(false)
	const [showProfile, setShowProfile] = useState(false)
	const [isTopUpModalVisible, setTopUpModalVisible] = useState(false)
	const { collapsed, navbarTitle } = useSelector(state => ({
			navbarTitle: state.uiConfig.navbarTitle,
			collapsed: state.uiConfig.collapsed,
		}),
		shallowEqual
	)
	const {user, settings} = useAppSelector()
	const avatar = React.useMemo(() => {
    return getAvatar(user)
  }, [user])

	const changeLanguage = (lang) => (event) => {
		event.preventDefault()
		i18n.changeLanguage(lang)
		setShowLanguage(false)
	}

	React.useEffect(() => {
		console.log({ lang: i18n.language })
		const click = (e) => {
			const containsLanguage = refLanguage && refLanguage.current && refLanguage.current.contains(e.target)
			const containsProfile = refProfile && refProfile.current && refProfile.current.contains(e.target)
			if (showLanguage && !containsLanguage) {
				setShowLanguage(!showLanguage)
			}
			if (showProfile && !containsProfile) {
				setShowProfile(!showProfile)
			}
		}
		document.addEventListener('click', click)
		return () => {
			document.removeEventListener('click', click)
		}
	}, [ showLanguage, showProfile, setShowLanguage, setShowProfile ])

	const dispatch = useDispatch()
	return (
		<>
			<NavBar >
				<NavItem data-sidebar-toggler={true}>
					<BurgerToggle collapsed={collapsed} />
				</NavItem>
				{/* <button
				onClick={() =>
					dispatch({
						type: 'SET_CONFIG_KEY',
						key: 'collapsed',
						value: !collapsed
					})
				}
				className="mx-4">
				<FiMenu size={20}/>
			</button> */}
				<NavItem>
					<NavName>
						{ navbarTitle }
					</NavName>
				</NavItem>
				<NavItem data-flex={true}></NavItem>
				{/* {settings.news_tg_channel && <a href={settings.news_tg_channel} target={`_blank`}
											className="hidden lg:flex relative
						btn btn-default btn-rounded btn-icon bg-transparent hover:bg-blue-50  hover:text-blue-600
						items-center justify-center ">
				<FaTelegram size={16} className="stroke-current mr-2"/>
				<span className={`hidden sm:block`}>{t(`navbar.tg_news`)}</span>
			</a>} */}
				<NavItem>
					{/*TODO: check the href-link after requirements are ready */}
					<NavLink as='a'
									 target="_blank"
									 href="#"
					>
						<IconSupport size={20} />
						{ t(`navbar.support`) }
					</NavLink>
				</NavItem>
				<NavItem>
					{/*TODO: check the href-link after requirements are ready */}
					<NavLink as='a'
									 target="_blank"
									 href={`https://invisible-1.gitbook.io/invisible-docs/`}
					>
						<IconDocumentation size={20} />
						{ t(`navbar.docs`) }
					</NavLink>
				</NavItem>
				<NavItem ref={refLanguage}>
					<NavDropdown data-color-hover={true}
											 data-active={showLanguage}
											 onClick={() => setShowLanguage(!showLanguage)}
					>
						<IconLanguage size={20} />
						{
							showLanguage
								? (<IconUp size={20} />)
								: (<IconDown size={20} />)
						}
					</NavDropdown>
					{
						showLanguage
							? <>
								<NavDropdownList>
									{
										Languages.values.map(lang => {
											const active = lang === i18n.language
											return (
												<NavDropdownLink href="#"
																				 key={`nav-dropdown-link-${lang}`}
																				 data-active={active}
																				 onClick={changeLanguage(lang)}
												>
													{lang}
												</NavDropdownLink>
											)
										})
									}
								</NavDropdownList>
							</>
							: null
					}
				</NavItem>
				<NavDelimiter />
				<NavItem ref={refProfile}>
					<NavDropdown data-background={false}
											 data-opacity-hover={true}
											 onClick={() => setShowProfile(!showProfile)}
					>
						<NavProfile>
							<NavAvatar style={{ backgroundImage: `url(${avatar})` }} />
							<NavProfileInfo>
								<NavProfileName className={`truncate`}>{ user.name }</NavProfileName>
								{
									settings.monetization_active || 1
										? (<NavProfileBalance>${ user.balance || 0.0 }</NavProfileBalance>)
										:  null
								}
							</NavProfileInfo>
						</NavProfile>
						{
							showProfile
								? (<IconUp size={20} />)
								: (<IconDown size={20} />)
						}
					</NavDropdown>
					{
						showProfile
							? <>
								<NavDropdownList data-profile={true}>
									<NavDropdownLink
										href="#"
										onClick = { (e) => {
											e.preventDefault();
											e.stopPropagation();
											setTopUpModalVisible(true)
										}}
									>
										<IconTopUp size={16} />
										{t('modal.topup.btn_save')}
									</NavDropdownLink>
									<NavDropdownLink href="/user/profile" >
										<IconProfile size={16} />
										{t('nav_menu.section_user.profile')}
									</NavDropdownLink>
									<NavDropdownLink href="/auth/logout" onClick={(e) => {
										e.preventDefault()
										dispatch(UserAction.logout())
									}} >
										<IconLogout size={16} />
										{t('general.logout')}
									</NavDropdownLink>
								</NavDropdownList>
							</>
							: null
					}
				</NavItem>

				{/* <Dropdown5/> */}
			</NavBar>

			<ModalTopUp
				isOpen={isTopUpModalVisible}
				onCancel={()=>setTopUpModalVisible(false)}
				// onConfirm={onTeamSubscriptionUpdated}
			/>
		</>
	)
}

export default Navbar
